<template>
    <div class="layout-px-spacing">
        <teleport to="#breadcrumb">
            <ul class="navbar-nav flex-row">
                <li>
                    <div class="page-header">
                        <nav class="breadcrumb-one" aria-label="breadcrumb">
                            <ol class="breadcrumb">
                                <li class="breadcrumb-item active" aria-current="page"><span>Traffic Fines</span></li>
                            </ol>
                        </nav>
                    </div>
                </li>
            </ul>
        </teleport>

        <div class="row layout-top-spacing">
            <div class="col-xl-12 col-lg-12 col-md-12 col-12 layout-spacing">
                <div class="panel br-4">
                    <div class="panel-header">
                        <div class="row">
                            <div class="col-xl-6 col-md-6 col-sm-6 col-12">
                                <h4>Traffic Fines</h4>
                            </div>
                        </div>
                    </div>
                    <div class="panel-body">
                        <!-- Filters and Search -->
                        <div class="row mb-4">
                            <div class="col-md-3">
                                <div class="form-group">
                                    <label>Penalty Filter</label>
                                    <select v-model="penaltyFilter" class="form-control">
                                        <option value="all">All</option>
                                        <option value="with">With Penalties</option>
                                        <option value="without">Without Penalties</option>
                                    </select>
                                </div>
                            </div>
                            <div class="col-md-4">
                                <div class="form-group">
                                    <label>Date Range</label>
                                    <div class="input-group">
                                        <input type="date" v-model="startDate" class="form-control"
                                            placeholder="Start Date">
                                        <input type="date" v-model="endDate" class="form-control"
                                            placeholder="End Date">
                                    </div>
                                </div>
                            </div>
                            <div class="col-md-5">
                                <div class="form-group">
                                    <label>Search</label>
                                    <input v-model="searchQuery" type="text" class="form-control"
                                        placeholder="Search by Plate, Route, Driver, Control No or Location...">
                                </div>
                            </div>
                        </div>

                        <!-- Loading State -->
                        <div v-if="isLoading" class="text-center mt-5">
                            <div class="spinner-border text-primary" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                        </div>

                        <!-- Table -->
                        <div v-else class="table-responsive">
                            <table role="table" aria-busy="false" class="table table-hover table-bordered">
                                <thead role="rowgroup">
                                    <tr role="row">
                                        <th role="columnheader" scope="col">S/N</th>
                                        <th role="columnheader" scope="col">Plate Number</th>
                                        <th role="columnheader" scope="col">Assigned Route</th>
                                        <th role="columnheader" scope="col">Driver Name</th>
                                        <th role="columnheader" scope="col">Control No</th>
                                        <th role="columnheader" scope="col">Location</th>
                                        <th role="columnheader" scope="col">Offence</th>
                                        <th role="columnheader" scope="col">Charge</th>
                                        <th role="columnheader" scope="col">Penalty</th>
                                        <th role="columnheader" scope="col">Status</th>
                                        <th role="columnheader" scope="col">Issued Date</th>
                                        <th role="columnheader" scope="col" class="text-center">Action</th>
                                    </tr>
                                </thead>
                                <tbody role="rowgroup">
                                    <tr v-for="(item, index) in filteredTrafficFines" :key="item.control_no" role="row">
                                        <td role="cell">{{ index + 1 }}</td>
                                        <td role="cell">{{ item.plate_number }}</td>
                                        <td role="cell">{{ item.assigned_route }}</td>
                                        <td role="cell">{{ item.driver_name }}</td>
                                        <td role="cell">{{ item.control_no }}</td>
                                        <td role="cell">{{ item.location }}</td>
                                        <td role="cell">{{ item.offence }}</td>
                                        <td role="cell">{{ item.charge }}</td>
                                        <td role="cell">{{ item.penalty }}</td>
                                        <td role="cell">
                                            <span :class="`badge badge-${getStatusClass(item.status)}`">
                                                {{ item.status }}
                                            </span>
                                        </td>
                                        <td role="cell">{{ formatDate(item.issued_date) }}</td>
                                        <td role="cell" class="text-center">
                                            <button @click="deleteItem(item)" class="btn btn-danger btn-sm">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                                    viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                    stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                                                    class="feather feather-trash-2">
                                                    <polyline points="3 6 5 6 21 6"></polyline>
                                                    <path
                                                        d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2">
                                                    </path>
                                                    <line x1="10" y1="11" x2="10" y2="17"></line>
                                                    <line x1="14" y1="11" x2="14" y2="17"></line>
                                                </svg>
                                            </button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <!-- Empty State -->
                        <div v-if="!isLoading && filteredTrafficFines.length === 0" class="text-center py-5">
                            <h5>No traffic fines found</h5>
                            <p class="text-muted">Try adjusting your filters or search query</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.layout-px-spacing {
    min-height: calc(100vh - 170px) !important;
}

.panel-header {
    padding: 20px;
    border-bottom: 1px solid #e0e6ed;
}

.badge {
    padding: 5px 10px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
}

.badge-paid {
    background-color: #d1fae5;
    color: #065f46;
}

.badge-unpaid {
    background-color: #fee2e2;
    color: #b91c1c;
}

.badge-pending {
    background-color: #fef3c7;
    color: #92400e;
}
</style>

<script setup>
import { onMounted, ref, computed } from 'vue';
import { useMeta } from '@/composables/use-meta';
import axios from 'axios';
import { useStore } from 'vuex';
import Swal from 'sweetalert2';
import { BASE_URL } from '@/Config/config';

useMeta({ title: 'Traffic Fines' });

const store = useStore();

// Reactive Variables
const isLoading = ref(false);
const trafficFines = ref([]);
const searchQuery = ref('');
const penaltyFilter = ref('all');
const startDate = ref('');
const endDate = ref('');

// Permission Check
const hasPrintTrafficFinesPermission = computed(() => store.getters.hasPermission('print_abs_unpaid_traffic_fines'));

// Computed Property for Filtered Data
const filteredTrafficFines = computed(() => {
    return trafficFines.value.filter(item => {
        // Search filter
        const matchesSearch = searchQuery.value === '' ||
            [item.plate_number, item.assigned_route, item.driver_name, item.control_no, item.location]
                .some(field => field?.toLowerCase().includes(searchQuery.value.toLowerCase()));

        // Penalty filter
        const matchesPenalty = penaltyFilter.value === 'all' ||
            (penaltyFilter.value === 'with' && item.penalty > 0) ||
            (penaltyFilter.value === 'without' && item.penalty === 0);

        // Date range filter
        let matchesDate = true;
        if (startDate.value || endDate.value) {
            const issuedDate = new Date(item.issued_date);
            const start = startDate.value ? new Date(startDate.value) : null;
            const end = endDate.value ? new Date(endDate.value) : null;

            if (start && issuedDate < start) matchesDate = false;
            if (end && issuedDate > end) matchesDate = false;
        }

        return matchesSearch && matchesPenalty && matchesDate;
    });
});

// Helper Functions
const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
        case 'paid': return 'paid';
        case 'unpaid': return 'unpaid';
        case 'pending': return 'pending';
        default: return 'secondary';
    }
};

const formatDate = (dateString) => {
    if (!dateString) return '';
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
};

const deleteItem = (item) => {
    Swal.fire({
        title: 'Are you sure?',
        text: `Delete traffic fine for ${item.plate_number}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
        if (result.isConfirmed) {
            // Add your delete logic here
            Swal.fire(
                'Deleted!',
                'The record has been deleted.',
                'success'
            );
        }
    });
};

// SweetAlert Configuration
const showErrorAlert = (message) => {
    Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: message,
        padding: '2em',
        confirmButtonColor: '#3085d6'
    });
};

const fetchTrafficFines = async () => {
    try {
        isLoading.value = true;
        const response = await axios.get(`${BASE_URL}/unpaid-traffic-fines`);
        trafficFines.value = response.data?.data || response.data || [];
    } catch (error) {
        console.error('Error fetching traffic fines:', error);
        showErrorAlert('Failed to fetch traffic fines. Please try again later.');
    } finally {
        isLoading.value = false;
    }
};

onMounted(async () => {
    await fetchTrafficFines();
});
</script>